// Smart Daara - Physical school dashboard (Directeur)
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { buildTenantPath } from '../../lib/routing';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { GraduationCap, Users, BookMarked, DoorOpen, CalendarDays, AlertTriangle, FolderOpen } from 'lucide-react';

export default function PhysicalDashboard() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const fr = language === 'fr';
  const p = (sub) => buildTenantPath(user?.tenant_slug, sub);

  useEffect(() => {
    physicalAPI.dashboard().then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>;

  const cards = [
    { icon: FolderOpen, label: 'Classes', value: stats?.classes ?? 0, path: 'school/physical/classes' },
    { icon: GraduationCap, label: fr ? 'Élèves' : 'Students', value: stats?.students ?? 0, path: 'school/physical/students' },
    { icon: Users, label: fr ? 'Enseignants' : 'Teachers', value: stats?.teachers ?? 0, path: 'school/physical/teachers' },
    { icon: BookMarked, label: fr ? 'Matières' : 'Subjects', value: stats?.subjects ?? 0, path: 'school/physical/subjects' },
    { icon: DoorOpen, label: fr ? 'Salles' : 'Rooms', value: stats?.rooms ?? 0, path: 'school/physical/rooms' },
    { icon: CalendarDays, label: fr ? 'Créneaux planifiés' : 'Scheduled slots', value: stats?.timetable_entries ?? 0, path: 'school/physical/timetable' },
  ];

  return (
    <div className="space-y-6" data-testid="physical-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {fr ? 'Gestion de l\'établissement' : 'School management'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {stats?.active_year
              ? `${fr ? 'Année scolaire active' : 'Active school year'} : ${stats.active_year.name}`
              : (fr ? 'Aucune année scolaire active — créez-la dans Année & paramètres' : 'No active school year')}
          </p>
        </div>
        <Badge className="bg-sky-100 text-sky-800 border-sky-200">{fr ? 'École physique' : 'Physical school'}</Badge>
      </div>

      {!stats?.active_year && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              {fr ? 'Commencez par créer l\'année scolaire.' : 'Start by creating the school year.'}
            </div>
            <Button size="sm" className="bg-emerald-700" onClick={() => navigate(p('school/physical/academic'))} data-testid="goto-academic-btn">
              {fr ? 'Année & paramètres' : 'Year & settings'}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(p(c.path))}>
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
                <c.icon className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{c.value}</p>
                <p className="text-sm text-slate-500">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stats?.uncovered_subjects?.length > 0 && (
        <Card className="border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
              <AlertTriangle className="w-4 h-4" />
              {fr ? `${stats.uncovered_subjects.length} matière(s) sans enseignant` : `${stats.uncovered_subjects.length} subject(s) without teacher`}
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.uncovered_subjects.slice(0, 12).map((u, i) => (
                <Badge key={i} variant="outline" className="text-red-600 border-red-200">{u.class} — {u.subject}</Badge>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate(p('school/physical/assignments'))}>
              {fr ? 'Gérer les affectations' : 'Manage assignments'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
